import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const fmt = (n) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

export const Calculator = ({ embedded = false }) => {
    const [amount, setAmount] = useState(750000);
    const [rate, setRate] = useState(6.19);
    const [years, setYears] = useState(30);

    const { monthly, totalRepay, totalInterest } = useMemo(() => {
        const r = rate / 100 / 12;
        const n = years * 12;
        const m = r === 0 ? amount / n : (amount * r) / (1 - Math.pow(1 + r, -n));
        const total = m * n;
        return { monthly: m, totalRepay: total, totalInterest: total - amount };
    }, [amount, rate, years]);

    return (
        <section data-testid="repayment-calculator" className={embedded ? "bg-[#F3F1EA]" : "bg-background"}>
            <div className="container-prose py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="overline">Tools</div>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A362B] font-medium">
                            Mortgage repayment calculator.
                        </h2>
                        <div className="divider-thin my-8" />
                        <p className="text-[#1C1C1C]/75 leading-relaxed">
                            Estimate your monthly principal &amp; interest repayments. Adjust the loan amount,
                            interest rate and term to see how it changes your repayments.
                        </p>
                        <p className="mt-4 text-xs text-[#1C1C1C]/50 leading-relaxed">
                            * Indicative only. Actual rates and repayments depend on your circumstances and lender.
                        </p>
                        <p data-testid="calc-disclaimer" className="mt-3 text-xs text-[#1C1C1C]/60 leading-relaxed italic border-l-2 border-[#C69C6D] pl-3">
                            Your complete financial situation will need to be assessed before acceptance of any proposal or product.
                        </p>
                    </div>

                    <div className="lg:col-span-7 bg-white border border-border p-8 md:p-10 rounded-sm">
                        <div className="space-y-8">
                            <div>
                                <div className="flex items-end justify-between mb-3">
                                    <Label htmlFor="calc-amount" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Loan amount</Label>
                                    <Input
                                        id="calc-amount"
                                        data-testid="calc-amount-input"
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                                        className="w-40 h-9 text-right font-serif text-lg text-[#1A362B] border-border"
                                    />
                                </div>
                                <Slider
                                    data-testid="calc-amount-slider"
                                    value={[amount]}
                                    min={50000}
                                    max={3000000}
                                    step={10000}
                                    onValueChange={(v) => setAmount(v[0])}
                                />
                                <div className="flex justify-between text-xs text-[#1C1C1C]/50 mt-2">
                                    <span>{fmt(50000)}</span>
                                    <span>{fmt(3000000)}</span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-end justify-between mb-3">
                                    <Label htmlFor="calc-rate" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Interest rate (% p.a.)</Label>
                                    <Input
                                        id="calc-rate"
                                        data-testid="calc-rate-input"
                                        type="number"
                                        step="0.01"
                                        value={rate}
                                        onChange={(e) => setRate(Math.max(0, Number(e.target.value) || 0))}
                                        className="w-28 h-9 text-right font-serif text-lg text-[#1A362B] border-border"
                                    />
                                </div>
                                <Slider
                                    data-testid="calc-rate-slider"
                                    value={[rate]}
                                    min={1}
                                    max={12}
                                    step={0.05}
                                    onValueChange={(v) => setRate(v[0])}
                                />
                                <div className="flex justify-between text-xs text-[#1C1C1C]/50 mt-2">
                                    <span>1.00%</span>
                                    <span>12.00%</span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-end justify-between mb-3">
                                    <Label htmlFor="calc-years" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Loan term (years)</Label>
                                    <Input
                                        id="calc-years"
                                        data-testid="calc-years-input"
                                        type="number"
                                        value={years}
                                        onChange={(e) => setYears(Math.max(1, Number(e.target.value) || 1))}
                                        className="w-24 h-9 text-right font-serif text-lg text-[#1A362B] border-border"
                                    />
                                </div>
                                <Slider
                                    data-testid="calc-years-slider"
                                    value={[years]}
                                    min={1}
                                    max={40}
                                    step={1}
                                    onValueChange={(v) => setYears(v[0])}
                                />
                                <div className="flex justify-between text-xs text-[#1C1C1C]/50 mt-2">
                                    <span>1 yr</span>
                                    <span>40 yrs</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 border-t border-border pt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div data-testid="calc-monthly">
                                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#C69C6D]">Monthly</div>
                                <div className="mt-2 font-serif text-3xl md:text-4xl text-[#1A362B] tracking-tight">{fmt(monthly)}</div>
                            </div>
                            <div data-testid="calc-total-repay">
                                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#C69C6D]">Total repaid</div>
                                <div className="mt-2 font-serif text-2xl text-[#1A362B] tracking-tight">{fmt(totalRepay)}</div>
                            </div>
                            <div data-testid="calc-total-interest">
                                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#C69C6D]">Interest paid</div>
                                <div className="mt-2 font-serif text-2xl text-[#1A362B] tracking-tight">{fmt(totalInterest)}</div>
                            </div>
                        </div>

                        <Link to="/contact" data-testid="calc-cta" className="btn-primary mt-10 w-full sm:w-auto">
                            Get a real rate from Gurpreet
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Calculator;
